import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './AuthContext';
import { apiService } from '@/services/apiService';

interface UserNote {
  id: string;
  content: string;
  timestamp: Date;
  type?: 'note' | 'trail' | 'sentiment' | 'checkin' | 'relapse' | 'user';
}

interface NotesContextType {
  userNotes: UserNote[];
  savedBillMessages: Set<number>;
  isLoading: boolean;
  addNote: (content: string, type?: 'note' | 'trail' | 'sentiment' | 'checkin' | 'relapse') => Promise<void>;
  updateNote: (id: string, content: string) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  saveBillMessage: (messageId: number) => void;
  unsaveBillMessage: (messageId: number) => void;
  isBillMessageSaved: (messageId: number) => boolean;
  refreshEntries: () => Promise<void>;
}

const NotesContext = createContext<NotesContextType | undefined>(undefined);

const NOTES_STORAGE_KEY = '@padrinho_user_notes';
const SAVED_MESSAGES_KEY = '@padrinho_saved_messages';

export function NotesProvider({ children }: { children: ReactNode }) {
  const [userNotes, setUserNotes] = useState<UserNote[]>([]);
  const [savedBillMessages, setSavedBillMessages] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  // CRITICAL FIX: Get session and sessionReady from AuthContext
  // sessionReady indicates the session has been validated and is safe for API calls
  const { user, session, sessionReady } = useAuth();

  useEffect(() => {
    if (user && sessionReady && session?.access_token) {
      // Only load from API when session is validated and ready
      console.log('User logged in with validated session, loading notes via API for user:', user.id);
      loadNotesFromAPI();
    } else if (user && !sessionReady) {
      // User exists but session not validated yet - wait for validation
      console.log('User logged in but waiting for session validation...');
    } else {
      console.log('No user, loading notes from local storage');
      loadNotesFromStorage();
    }
    loadSavedMessages();
  }, [user, session, sessionReady]);

  const loadNotesFromAPI = async () => {
    if (!user) {
      console.log('No user found, falling back to local storage');
      loadNotesFromStorage();
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.get('/api/diary/notes');

      if (!response.success || response.status === 401) {
        // Soft-fail: API hiccup or auth race during boot. We fall back
        // to local storage so the user keeps reading their notes —
        // logged as warn (not error) so Expo's LogBox does not flash
        // a red overlay for a recoverable degradation.
        console.warn('⚠️ NotesContext: notes API unavailable, using local cache:', response.error);
        loadNotesFromStorage();
        return;
      }

      const formattedNotes: UserNote[] = response.data?.notes?.map((note: any) => ({
        id: note.id,
        content: note.content,
        timestamp: new Date(note.updated_at || note.created_at),
        type: note.type || 'note',
      })) || [];

      setUserNotes(formattedNotes);
      console.log('Notes loaded via API:', { count: formattedNotes.length });
    } catch (error) {
      // Network blip / abort — falls back to local cache (see comment above).
      console.warn('⚠️ NotesContext: notes API threw, using local cache:', error);
      loadNotesFromStorage();
    } finally {
      setIsLoading(false);
    }
  };

  const loadNotesFromStorage = async () => {
    setIsLoading(true);
    try {
      const stored = await AsyncStorage.getItem(NOTES_STORAGE_KEY);
      if (stored) {
        const parsedNotes = JSON.parse(stored);
        const notesWithDates = parsedNotes.map((note: any) => ({
          ...note,
          timestamp: new Date(note.timestamp)
        }));
        setUserNotes(notesWithDates);
      } else {
        setUserNotes([]);
      }
    } catch (error) {
      console.error('Error loading notes from storage:', error);
      setUserNotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSavedMessages = async () => {
    try {
      const stored = await AsyncStorage.getItem(SAVED_MESSAGES_KEY);
      if (stored) {
        const parsedMessages = JSON.parse(stored);
        setSavedBillMessages(new Set(parsedMessages));
      }
    } catch (error) {
      console.error('Error loading saved messages:', error);
    }
  };

  const saveNotesToStorage = async (notes: UserNote[]) => {
    try {
      await AsyncStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
    } catch (error) {
      console.error('Error saving notes to storage:', error);
    }
  };

  const saveSavedMessages = async (messages: Set<number>) => {
    try {
      await AsyncStorage.setItem(SAVED_MESSAGES_KEY, JSON.stringify([...messages]));
    } catch (error) {
      console.error('Error saving messages to storage:', error);
    }
  };

  const addNote = async (content: string, type?: 'note' | 'trail' | 'sentiment' | 'checkin' | 'relapse') => {
    const tempId = Date.now().toString();
    const trimmed = content.trim();
    const newNote: UserNote = {
      id: tempId,
      content: trimmed,
      timestamp: new Date(),
    };

    const updatedNotes = [newNote, ...userNotes];
    setUserNotes(updatedNotes);

    // Fire-and-forget Clarity event. Length is numeric, type is an enum —
    // neither leaks note content (LGPD).
    import('@/utils/clarityEvents')
      .then(({ logClarityEvent }) =>
        logClarityEvent('note_created', {
          length: trimmed.length,
          type: type || 'note',
          authenticated: !!user,
        })
      )
      .catch(() => {});

    if (user) {
      try {
        const response = await apiService.post('/api/diary/notes', { content: trimmed, type: type || 'note' });

        if (!response.success) {
          // Soft-fail: optimistic note already showing; persist to local
          // cache so it survives a reload. Warn instead of error to keep
          // Expo's red LogBox quiet for a recoverable degradation.
          console.warn('⚠️ NotesContext: add-note API failed, persisting locally:', response.error);
          saveNotesToStorage(updatedNotes);
          return;
        }

        const apiNote: UserNote = {
          id: response.data.note.id,
          content: response.data.note.content,
          timestamp: new Date(response.data.note.created_at),
        };

        setUserNotes(prev => prev.map(note => 
          note.id === tempId ? apiNote : note
        ));

        // Fire-and-forget: confirms server-side persistence completed.
        // noteId is an opaque server UUID, not PII.
        import('@/utils/clarityEvents')
          .then(({ logClarityEvent }) =>
            logClarityEvent('note_saved', {
              noteId: apiNote.id,
              type: type || 'note',
              persisted: 'remote',
            })
          )
          .catch(() => {});
      } catch (error) {
        console.warn('⚠️ NotesContext: add-note threw, persisting locally:', error);
        saveNotesToStorage(updatedNotes);
      }
    } else {
      saveNotesToStorage(updatedNotes);
      // Local-only save still counts as a save from the user's perspective.
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent('note_saved', {
            type: type || 'note',
            persisted: 'local',
          })
        )
        .catch(() => {});
    }
  };

  const updateNote = async (id: string, content: string) => {
    const updatedNotes = userNotes.map(note => 
      note.id === id 
        ? { ...note, content: content.trim(), timestamp: new Date() }
        : note
    );
    setUserNotes(updatedNotes);

    if (user) {
      try {
        const response = await apiService.put(`/api/diary/notes/${id}`, { content: content.trim() });

        if (!response.success) {
          console.warn('⚠️ NotesContext: update-note API failed, persisting locally:', response.error);
          saveNotesToStorage(updatedNotes);
          return;
        }

        console.log('Note updated via API:', { id });
      } catch (error) {
        console.warn('⚠️ NotesContext: update-note threw, persisting locally:', error);
        saveNotesToStorage(updatedNotes);
      }
    } else {
      saveNotesToStorage(updatedNotes);
    }
  };

  const deleteNote = async (id: string) => {
    const updatedNotes = userNotes.filter(note => note.id !== id);
    setUserNotes(updatedNotes);

    if (user) {
      try {
        const response = await apiService.delete(`/api/diary/notes/${id}`);

        if (!response.success) {
          // Soft-fail: roll the optimistic delete back by re-fetching
          // the canonical list. Warn (not error) — recoverable.
          console.warn('⚠️ NotesContext: delete-note API failed, re-syncing list:', response.error);
          loadNotesFromAPI();
          return;
        }

        console.log('Note deleted via API:', { id });
      } catch (error) {
        console.warn('⚠️ NotesContext: delete-note threw, re-syncing list:', error);
        loadNotesFromAPI();
      }
    } else {
      saveNotesToStorage(updatedNotes);
    }
  };

  const saveBillMessage = (messageId: number) => {
    const newSet = new Set([...savedBillMessages, messageId]);
    setSavedBillMessages(newSet);
    saveSavedMessages(newSet);
  };

  const unsaveBillMessage = (messageId: number) => {
    const newSet = new Set(savedBillMessages);
    newSet.delete(messageId);
    setSavedBillMessages(newSet);
    saveSavedMessages(newSet);
  };

  const isBillMessageSaved = (messageId: number) => {
    return savedBillMessages.has(messageId);
  };

  const refreshEntries = async () => {
    if (user) {
      await loadNotesFromAPI();
    } else {
      await loadNotesFromStorage();
    }
  };

  return (
    <NotesContext.Provider
      value={{
        userNotes,
        savedBillMessages,
        isLoading,
        addNote,
        updateNote,
        deleteNote,
        saveBillMessage,
        unsaveBillMessage,
        isBillMessageSaved,
        refreshEntries,
      }}
    >
      {children}
    </NotesContext.Provider>
  );
}

export const useNotes = () => {
  const context = useContext(NotesContext);
  if (context === undefined) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
};
